import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import DebugService from '../services/DebugService';
import ASGInventoryTable from '../components/common/inventory/ASGInventoryTable';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Popover,
  Select,
  Tab,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TextField,
} from '@mui/material';
import CustomTable from '../components/common/Table.jsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import APIConstants from '../services/APIConstants';
import {
  InstanceActionList,
  asgActivityLogColumns,
  asgColumnsForInventory,
  asgModificationLogColumns,
  asgModificationLogColumnsForChanges,
  ec2ActivityLogColumnsForDataGrid,
  ec2ColumnsForDataGrid,
  ec2ColumnsForIdleEvents,
  ec2ColumnsForScheduledEvents,
  ec2ModificationChangesColumns,
  ec2ModificationLogColumnsForDataGrid,
  rdsColumnsForActivityevents,
  rdsColumnsForIdleEvents,
  rdsColumnsForInventory,
  rdsColumnsForModificationEvents,
  rdsColumnsForScheduledEvents,
  rdsModificationChangesColumns
} from '../services/InventoryService';
import {
  Ec2InstanceDetails,
} from '../state/Zuststore';
import SnackbarNotification, { SNACKBAR_THEME } from '../components/common/SnackbarNotification';
import InventoryApiService from '../services/InventoryApiService';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildExportFilename,
  CONST_SITE_LABEL,
  INVENTORY_EVENTS,
  setScopedItem,
  STORAGE_KEYS,
  SUBSCRIPTION_STATUS,
  TRIAL_SUBSCRIPTION_DATA_LIMIT,
  useCurrentInstanceType,
  USER_ROLE,
  yesterdayDate
} from '../components/common/Helper';
import InstanceScheduler from '../components/common/inventory/InstanceScheduler';
import { HiOutlineRefresh } from 'react-icons/hi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { FaRegEdit } from 'react-icons/fa';
import { format } from 'date-fns';
import ConfirmModal from '../components/common/ConfirmModal';
import dayjs from 'dayjs';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TimezoneSelect from 'react-timezone-select';
import ModifyASG from '../components/common/inventory/ModifyASG';
import SubscriptionApiService from '../services/SubscriptionApiService';
import { useAuth } from '../state/AuthContext';
import ScheduleIdle from '../components/common/inventory/ScheduleIdle';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

const InstanceIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
    <path d="M9 3v3M12 3v3M15 3v3M9 18v3M12 18v3M15 18v3M3 9h3M3 12h3M3 15h3M18 9h3M18 12h3M18 15h3" />
  </svg>
);

const mapGridColumnsToTableColumns = (gridCols) => {
  return gridCols.map((col) => ({
    key: col.field,
    label: col.headerName ?? col.field,
    className: col.className ?? (col.align === 'right' ? 'num' : col.align === 'center' ? 'center' : ''),
    render: col.renderCell ? (row) => col.renderCell({ row, value: row[col.field] }) : undefined
  }));
};

export default function EC2Inventory() {
  const [selectedTab, setSelectedTab] = useState(INVENTORY_EVENTS.INSTANCES);
  const location = useLocation();
  const { type } = useCurrentInstanceType();

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | EC2 Inventory`;
  }, [location.pathname]);

  const inventoryTabs = [
    INVENTORY_EVENTS.INSTANCES,
    INVENTORY_EVENTS.SCHEDULED,
    INVENTORY_EVENTS.IDLE,
    INVENTORY_EVENTS.ACTIVITY,
    INVENTORY_EVENTS.MODIFICATION,
  ];

  return (
    <div>
      <div className="breadcrumb">
        Inventory <span className="breadcrumb-separator">›</span> <strong>EC2 Instance</strong>
      </div>
      <div className="tab-row" style={{ flexWrap: 'wrap', marginBottom: '16px' }}>
        {inventoryTabs.map(tab => (
          <div
            key={tab}
            className={`tab${selectedTab === tab ? ' active' : ''}`}
            onClick={() => setSelectedTab(tab)}
          >
            {tab}
          </div>
        ))}
      </div>
      <div>
        {selectedTab === INVENTORY_EVENTS.INSTANCES && <CurrentInstanceComponent />}
        {selectedTab === INVENTORY_EVENTS.SCHEDULED && <ScheduledEvents />}
        {selectedTab === INVENTORY_EVENTS.IDLE && <IdleEventsComponent />}
        {selectedTab === INVENTORY_EVENTS.ACTIVITY && <ActivityLogComponent />}
        {selectedTab === INVENTORY_EVENTS.MODIFICATION && <ModificationLogComponent />}
      </div>
    </div>
  );
}

export function CurrentInstanceComponent() {
  const [Opendialog, setOpendialog] = useState(false);
  const [openModifyASGDialog, setOpenModifyASGDialog] = useState(false);
  const [openScheduleIdleDialog, setOpenScheduleIdleDialog] = useState(false);
  const [instanceAction, setInstanceAction] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [actionStatusMessage, setActionStatusMessage] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [filterTagList, setFilterTagList] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showActivateNowConfirmModal, setShowActivateNowConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [totalInstancesCount, setTotalInstancesCount] = useState(0);
  const [totalRunningInstancesCount, setTotalRunningInstancesCount] = useState(0);
  const [totalStoppedInstancesCount, setTotalStoppedInstancesCount] = useState(0);
  const [currentUserAccess, setCurrentUserAccess] = useState(null);
  const [defaultHideColumnsForCustom, setDefaultHideColumnsForCustom] = React.useState({});
  const [actionBy, setActionBy] = React.useState('DROPDOWN');
  const [paginationData, setPaginationData] = useState({ pageSize: 25, currentPage: 1 });
  const [filterData, setFilterData] = useState({ field: '', value: '', tagField: '', tagValue: [] });
  const [totalInstancesApiData, setTotalInstancesApiData] = useState(null);

  const didMountRef = useRef(false);
  const refetchAfterFieldChange = useRef(false);

  const setInstanceDetails = Ec2InstanceDetails((state) => state.setInstanceDetails);
  
  const [asgInstancesAnchorEl, setAsgInstancesAnchorEl] = useState(null);
  const [asgInstancesData, setAsgInstancesData] = useState([]);
  const openAsgInstances = Boolean(asgInstancesAnchorEl);

  const handleAsgInstancesClick = (e, instances) => {
    setAsgInstancesAnchorEl(e.currentTarget);
    setAsgInstancesData(instances);
  };

  const allSelected = rows.length > 0 && rows.every(r => selectedInstances.some(sel => sel.id === r.id));
  
  const toggleAllSelected = () => {
    if (allSelected) {
      setSelectedInstances(prev => prev.filter(p => !rows.some(r => r.id === p.id)));
    } else {
      setSelectedInstances(prev => {
        const toAdd = rows.filter(r => !prev.some(p => p.id === r.id));
        return [...prev, ...toAdd];
      });
    }
  };

  const toggleRowSelected = (row) => {
    setSelectedInstances(prev => {
      const exists = prev.some(p => p.id === row.id);
      if (exists) {
        return prev.filter(p => p.id !== row.id);
      } else {
        return [...prev, row];
      }
    });
  };

  const checkboxCol = {
    key: 'checkbox',
    label: (
      <span
        className={`checkbox ${allSelected ? 'checked' : ''}`}
        onClick={toggleAllSelected}
      />
    ),
    className: 'center',
    render: (row) => (
      <span
        className={`checkbox ${selectedInstances.some((sel) => sel.id === row.id) ? 'checked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleRowSelected(row);
        }}
      />
    ),
  };
  const location = useLocation();
  const { type } = useCurrentInstanceType();
  const { userDetails, refetchUserDetails, selectedAccount } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();
  const navigate = useNavigate();

  const ec2FilterFieldList = [
    { label: 'Instance Id', value: 'instanceId' },
    { label: 'Name', value: 'instanceName' },
    { label: 'Instance Type', value: 'instanceType' },
    { label: 'Instance Status', value: 'instanceStatus' },
    { label: 'Public Ip', value: 'publicIp' },
    { label: 'Tag', value: 'tags' },
  ];

  const asgFilterFieldList = [
    { label: 'Name', value: 'AutoScalingGroupNames' },
    { label: 'Tag', value: 'tags' },
  ];

  const rdsFilterFieldList = [{ label: 'Tag', value: 'tags' }];

  const ec2ActionGridColumn = {
    field: 'action',
    headerName: 'Action',
    align: 'center',
    disableExport: true,
    renderCell: (params) => {
      const state = params['row']['instanceState']?.toLowerCase() ?? '';
      return ['running', 'stopped'].includes(state) && (
        <Link
          className="action-link"
          title={state === 'running' ? 'Click to Stop' : 'Click to Start'}
          onClick={(e) => {
            e.preventDefault();
            handleGridColumnAction(state === 'running' ? 'STOP_NOW' : 'START_NOW', params['row']['id']);
          }}
          href="#"
        >
          {state === 'running' ? 'Stop Now' : 'Start Now'}
        </Link>
      );
    },
  };

  const rdsActionGridColumn = {
    field: 'action',
    headerName: 'Action',
    align: 'center',
    disableExport: true,
    renderCell: (params) => {
      const status = params['row']['Status']?.toLowerCase() ?? '';
      return ['stopped', 'available'].includes(status) && (
        <Link
          className="action-link"
          title={status === 'available' ? 'Click to Stop' : 'Click to Start'}
          onClick={(e) => {
            e.preventDefault();
            handleGridColumnAction(status === 'available' ? 'STOP_NOW' : 'START_NOW', params['row']['id']);
          }}
          href="#"
        >
          {status === 'available' ? 'Stop Now' : 'Start Now'}
        </Link>
      );
    },
  };

  const dataColumns =
    type === 'rds'
      ? currentUserAccess === USER_ROLE.READ_ONLY
        ? [...rdsColumnsForInventory]
        : [...rdsColumnsForInventory, rdsActionGridColumn]
      : type === 'ec2'
      ? currentUserAccess === USER_ROLE.READ_ONLY
        ? [...ec2ColumnsForDataGrid]
        : [...ec2ColumnsForDataGrid, ec2ActionGridColumn]
      : asgColumnsForInventory;

  const fetchData = async () => {
    setShowLoading(true);
    DebugService.log('Fetching Inventory Data...');

    try {
      setRows([]);
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS;
      if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG;

      const dataRequest = { ...paginationData, isForceRefresh: refresh };

      InventoryApiService.getInventory(selectedAccount, endpoint, dataRequest, filterData)
        .then((res) => {
          DebugService.log('Inventory: Data', res);

          if (res['data']) {
            let testData = res['data']['instances'] || [];

            if (
              (userDetails['subscriptionStatus'] && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(userDetails['subscriptionStatus'])) ||
              (getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS) && [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS)))
            ) {
              testData = testData.slice(0, TRIAL_SUBSCRIPTION_DATA_LIMIT);
            }

            setTotalInstancesApiData(res);
            setTotalInstancesCount(res['data']['totalInstanceCount'] ?? 0);
            setTotalRunningInstancesCount(res['data']['runningInstanceCount'] ?? 0);
            setTotalStoppedInstancesCount(res['data']['stoppedInstanceCount'] ?? 0);

            if (testData.length > 0) {
              if (type === 'rds') {
                setRows(
                  testData.map((instance) => ({
                    ...instance,
                    DBInstanceIdentifierList: instance['DBClusterMembers'].map((m) => m['DBInstanceIdentifier']).join(', '),
                    VpcSecurityGroupId: instance['VpcSecurityGroups']?.[0]?.['VpcSecurityGroupId'] ?? '-',
                    savingCost: instance['TotalSavingCost'],
                    IdleDays: `${instance['IdleDays']} Day(s)`,
                    MaximumCPUValue: (instance['MaximumCPUValue'] ?? 0).toFixed(2),
                    action: handleGridColumnAction,
                  }))
                );
              } else if (type === 'asg') {
                setRows(testData.map((i) => ({ id: i['AutoScalingGroupName'], ...i })));
                setTotalInstancesCount(res['data']['totalASGCount'] ?? 0);
                setTotalRunningInstancesCount(res['data']['runningInstanceCount'] ?? 0);
                setTotalStoppedInstancesCount(res['data']['unUsedInstanceCount'] ?? 0);
              } else {
                setRows(
                  testData.map((instance) => ({
                    ...instance,
                    id: instance.InstanceId,
                    name: instance.Tags.find((tag) => tag.Key === 'Name')?.Value,
                    instanceState: instance.State.Name,
                    instanceType: instance.InstanceType,
                    region: instance.Region,
                    tags: instance.Tags || [],
                    publicIPv4DNS: instance.PublicDnsName,
                    publicIPv4Address: instance.PublicIpAddress,
                    iPv6IPs: instance?.Ipv6Addresses?.length > 0 ? instance.Ipv6Addresses.join(', ') : '-',
                    monitoring: instance.Monitoring?.State,
                    securityGroupName: instance.SecurityGroups?.[0]?.GroupName,
                    keyName: instance.KeyName,
                    launchTime: format(instance.LaunchTime, 'dd-MM-yyyy HH:mm:ss'),
                    platfromDetails: instance.PlatformDetails,
                    savingCost: instance['TotalSavingCost'],
                    MaximumCPUValue: (instance['MaximumCPUValue'] ?? 0).toFixed(2),
                    IdleDays: `${instance['IdleDays']} Day(s)`,
                    action: handleGridColumnAction,
                  }))
                );
              }
            }
          }
          setShowLoading(false);
        })
        .catch((err) => {
          setShowLoading(false);
          DebugService.error('Inventory: Error caught while fetching data', err);
        });
    } catch (error) {
      setShowLoading(false);
      DebugService.error('Current Inventory Type: Fetch data error', error);
    } finally {
      setRefresh(false);
    }
  };

  const fetchTagList = async () => {
    DebugService.log('Fetching Inventory Tag List ...');
    try {
      setFilterTagList([]);
      const dataRequest = { type: type.toLocaleUpperCase(), userAccountId: selectedAccount };
      InventoryApiService.getFilterTagList(APIConstants.ENDPOINT_AWS_FILTER_TAG, dataRequest)
        .then((res) => {
          DebugService.log('Inventory: Data', res);
          setFilterTagList(res['data'] ?? []);
        })
        .catch((err) => DebugService.error('Inventory: Error caught while fetching filter tag list', err));
    } catch (error) {
      DebugService.error('Inventory: Error caught while fetching filter tag list', error);
    } finally {
      setRefresh(false);
    }
  };

  useEffect(() => {
    DebugService.log('Inventory: Path changed');
    fetchData();

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);
    setCurrentUserAccess(parseToJson['roleName']);

    if (type === 'rds') {
      setDefaultHideColumnsForCustom({ id: false });
    } else {
      setDefaultHideColumnsForCustom({});
    }

    onReset();
    fetchTagList();
  }, [location.pathname]);

  useEffect(() => {
    DebugService.log('Inventory: On pagination change');
    if (!didMountRef.current) { didMountRef.current = true; return; }
    fetchData();
  }, [paginationData, selectedAccount]);

  useEffect(() => {
    if (refresh) {
      setTimeout(() => {
        DebugService.log('Inventory: Force refresh is called');
        fetchData();
      }, 1000);
    }
  }, [refresh]);

  const instantActionHandler = async (action, data) => {
    setShowLoading(true);
    setRefresh(false);

    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);

    const dataRequest = {
      userAccountId: '',
      userId: '',
      status: action === InstanceActionList.START ? InstanceActionList.START : InstanceActionList.STOP,
    };

    if (selectedAccount) dataRequest['userAccountId'] = selectedAccount;
    if (parseToJson['userId']) dataRequest['userId'] = parseToJson['userId'];

    DebugService.log(`Selected instances for ${action}`, data);

    if (data.length > 0) {
      if (type === 'rds') {
        dataRequest['dbInstanceIdentifiers'] = [];
        dataRequest['dbClusterIdentifiers'] = [];

        for (let i = 0; i < data.length; i++) {
          if (data[i]['type'] === 'cluster') {
            dataRequest['dbClusterIdentifiers'] = [
              ...dataRequest['dbClusterIdentifiers'],
              {
                DBClusterIdentifier: data[i]['DBClusterIdentifier'],
                region: data[i]['Region'],
                DBInstanceIdentifiers: data[i]['DBClusterMembers'].map((m) => m['DBInstanceIdentifier']),
                tagValues: data[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
              },
            ];
          } else {
            dataRequest['dbInstanceIdentifiers'] = [
              ...dataRequest['dbInstanceIdentifiers'],
              {
                DBInstanceIdentifier: data[i]['DBClusterMembers'][0]['DBInstanceIdentifier'],
                region: data[i]['Region'],
                DBInstanceClass: data[i]['DBClusterMembers'][0]['DBInstanceClass'],
                tagValues: data[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
              },
            ];
          }
        }
      } else {
        dataRequest['instances'] = [];
        for (let i = 0; i < data.length; i++) {
          dataRequest['instances'] = [
            ...dataRequest['instances'],
            {
              instanceId: data[i]['id'],
              instanceName: data[i]['name'],
              region: data[i]['region'],
              instanceType: data[i]['instanceType'],
              tagValues: data[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
            },
          ];
        }
      }
    }

    if (
      (dataRequest['instances'] && dataRequest['instances'].length > 0) ||
      (dataRequest['dbInstanceIdentifiers'] && dataRequest['dbInstanceIdentifiers'].length > 0) ||
      (dataRequest['dbClusterIdentifiers'] && dataRequest['dbClusterIdentifiers'].length > 0)
    ) {
      DebugService.log(`Current Instance Component: ${action} instances initiated`, dataRequest);
      const endpoint = type === 'rds' ? APIConstants.ENDPOINT_INVENTORY_RDS_EVENTS : APIConstants.ENDPOINT_INVENTORY_EC2_EVENTS;

      await InventoryApiService.instantAction(endpoint, dataRequest)
        .then((res) => {
          DebugService.log(`Inventory Component: Instance ${dataRequest['status']} response`, res);
          if (res['data']) {
            if (res['data'][0]['error']) {
              setActionStatusMessage(res['data'][0]['error'] ?? 'Something went wrong');
              setActionStatus('failed');
            } else {
              setTimeout(() => {
                setActionStatusMessage(null);
                setActionStatus(null);
                setRefresh(true);
              }, 10000);
              onReset();
              setActionStatusMessage(`Instance ${dataRequest['status']} requested successfully. It will take sometime to reflect.`);
              setActionStatus('success');
            }
          } else {
            setActionStatusMessage(res['error']['message'] ?? 'Something went wrong');
            setActionStatus('failed');
          }
        })
        .catch((err) => {
          DebugService.error(`Inventory Component: Instance ${dataRequest['status']} failure`, err);
          setActionStatusMessage(`Error occurred while ${dataRequest['status']}ing instance`);
          setActionStatus('failed');
        })
        .finally(() => setShowLoading(false));
    }
  };

  const onScheduleLaterSave = async ({ startDate, stopDate, recurringOption, selectedDays, desireCount, minimumCount, timezone }) => {
    setShowLoading(true);

    let selectedRunningInstances = '';
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);

    const dataRequest = {
      userAccountId: selectedAccount ?? '',
      userId: '',
      startInstanceTime: startDate,
      stopInstanceTime: stopDate,
      type: recurringOption,
    };

    if (parseToJson['userId']) dataRequest['userId'] = parseToJson['userId'];
    if (recurringOption === 'WEEKLY') {
      dataRequest['days'] = selectedDays;
      dataRequest['timezone'] = timezone;
    }

    if (type === 'rds') {
      dataRequest['dbInstanceIdentifiers'] = [];
      dataRequest['dbClusterIdentifiers'] = [];
      selectedRunningInstances = selectedInstances.filter((i) => i['Status'] === 'available');

      for (let i = 0; i < selectedRunningInstances.length; i++) {
        if (selectedRunningInstances[i]['type'] === 'cluster') {
          dataRequest['dbClusterIdentifiers'] = [
            ...dataRequest['dbClusterIdentifiers'],
            {
              DBClusterIdentifier: selectedRunningInstances[i]['DBClusterIdentifier'],
              region: selectedRunningInstances[i]['Region'],
              DBInstanceIdentifiers: selectedRunningInstances[i]['DBInstanceIdentifierList'].split(', '),
              tagValues: selectedRunningInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
            },
          ];
        } else {
          dataRequest['dbInstanceIdentifiers'] = [
            ...dataRequest['dbInstanceIdentifiers'],
            {
              DBInstanceIdentifier: selectedRunningInstances[i]['DBClusterMembers'][0]['DBInstanceIdentifier'],
              region: selectedRunningInstances[i]['Region'],
              DBInstanceClass: selectedRunningInstances[i]['DBClusterMembers'][0]['DBInstanceClass'],
              tagValues: selectedRunningInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
            },
          ];
        }
      }
    } else if (type === 'asg') {
      dataRequest['asgList'] = [];
      for (let i = 0; i < selectedInstances.length; i++) {
        dataRequest['asgList'] = [
          ...dataRequest['asgList'],
          {
            AutoScalingGroupName: selectedInstances[i]['id'],
            currentDesiredCapacity: selectedInstances[i]['DesiredCapacity'] ?? 0,
            currentMinSize: selectedInstances[i]['MinSize'] ?? 0,
            currentMaxSize: selectedInstances[i]['MaxSize'] ?? 0,
            DesiredCapacity: desireCount ?? 0,
            MinSize: minimumCount ?? 0,
            Region: selectedInstances[i]['Region'],
            InstanceIds: selectedInstances[i]['Instances']?.map((i) => i['InstanceId']),
            tagValues: selectedInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
          },
        ];
      }
    } else {
      dataRequest['instances'] = [];
      selectedRunningInstances = selectedInstances.filter((i) => i['instanceState'] === 'running');
      for (let i = 0; i < selectedRunningInstances.length; i++) {
        dataRequest['instances'] = [
          ...dataRequest['instances'],
          {
            instanceId: selectedRunningInstances[i]['id'],
            instanceName: selectedRunningInstances[i]['name'],
            region: selectedRunningInstances[i]['region'],
            instanceType: selectedRunningInstances[i]['instanceType'],
            tagValues: selectedRunningInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
          },
        ];
      }
    }

    DebugService.log('Inventory Component: Schedule later initiated', dataRequest);

    let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_SCHEDULE_EVENTS;
    if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_SCHEDULE_EVENTS;
    if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_SCHEDULE_EVENTS;

    await InventoryApiService.scheduleEvents(endpoint, dataRequest)
      .then((res) => {
        DebugService.log('Inventory Component: Schedule later response', res);
        if (res['error']) {
          setActionStatusMessage(`${res['error']['message'] ?? 'Something went wrong'}`);
          setActionStatus('failed');
        } else {
          const errorList = res['data'].filter((d) => d['error'] !== undefined).map((d) => d['error']);
          if (errorList.length > 0) {
            setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong');
            setActionStatus('failed');
          } else {
            setTimeout(() => {
              setActionStatusMessage(null);
              setActionStatus(null);
              setRefresh(true);
            }, 10000);
            setOpendialog(false);
            setActionStatusMessage('Schedule later requested successfully. It will take sometime to reflect.');
            setActionStatus('success');
          }
        }
      })
      .catch((err) => {
        DebugService.error('Inventory Component: Schedule later failure', err);
        setActionStatusMessage('Error occurred while scheduling later');
        setActionStatus('failed');
      })
      .finally(() => setShowLoading(false));
  };

  const onModifyASGSave = async ({ desireCount, minimumCount }) => {
    setShowLoading(true);
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);

    const asgStatus = instanceAction === 'SCALE_DOWN_ASG' ? 'scale_down' : 'scale_default';
    const dataRequest = { userAccountId: selectedAccount ?? '', userId: '', status: asgStatus, asgList: [] };
    if (parseToJson['userId']) dataRequest['userId'] = parseToJson['userId'];

    if (selectedInstances.length > 0) {
      for (let i = 0; i < selectedInstances.length; i++) {
        dataRequest['asgList'] = [
          ...dataRequest['asgList'],
          {
            AutoScalingGroupName: selectedInstances[i]['id'],
            currentDesiredCapacity: selectedInstances[i]['DesiredCapacity'],
            currentMinSize: selectedInstances[i]['MinSize'],
            currentMaxSize: selectedInstances[i]['MaxSize'],
            DesiredCapacity: desireCount === null ? null : parseInt(desireCount, 10),
            MinSize: minimumCount === null ? null : parseInt(minimumCount, 10),
            Region: selectedInstances[i]['Region'],
            InstanceIds: selectedInstances[i]['Instances']?.map((i) => i['InstanceId']),
            tagValues: selectedInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
          },
        ];
      }
    }

    DebugService.log('Inventory Component: Modify ASG initiated', dataRequest);

    await InventoryApiService.scheduleEvents(APIConstants.ENDPOINT_INVENTORY_ASG_EVENTS, dataRequest)
      .then((res) => {
        DebugService.log('Inventory Component: Modify ASG response', res);
        if (res['error']) {
          setActionStatusMessage(res['error']['message'] ?? 'Something went wrong');
          setActionStatus('failed');
        } else {
          const errorList = res['data'].filter((d) => d['error'] !== undefined).map((d) => d['error']);
          if (errorList.length > 0) {
            setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong');
            setActionStatus('failed');
          } else {
            setTimeout(() => {
              setActionStatusMessage(null);
              setActionStatus(null);
              setRefresh(true);
            }, 10000);
            setOpenModifyASGDialog(false);
            setActionStatusMessage('ASG modified successfully');
            setActionStatus('success');
          }
        }
      })
      .catch((err) => {
        DebugService.error('Inventory Component: Modify ASG failure', err);
        setActionStatusMessage('Error occurred while modifying');
        setActionStatus('failed');
      })
      .finally(() => {
        setOpendialog(false);
        setShowLoading(false);
      });
  };

  const onScheduleIdleSave = async ({ idleDuration }) => {
    setShowLoading(true);
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);

    const dataRequest = { userAccountId: selectedAccount ?? '', userId: '' };
    if (parseToJson['userId']) dataRequest['userId'] = parseToJson['userId'];

    if (type === 'rds') {
      dataRequest['dbClusterIdentifiers'] = [];
      dataRequest['dbInstanceIdentifiers'] = [];
      for (let i = 0; i < selectedInstances.length; i++) {
        if (selectedInstances[i]['type'] === 'cluster') {
          dataRequest['dbClusterIdentifiers'] = [
            ...dataRequest['dbClusterIdentifiers'],
            {
              DBClusterIdentifier: selectedInstances[i]['DBClusterIdentifier'],
              DBInstanceIdentifiers: selectedInstances[i]['DBClusterMembers'].map((d) => d['DBInstanceIdentifier']),
              region: selectedInstances[i]['Region'],
              idleDays: idleDuration,
              tagValues: selectedInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
            },
          ];
        } else {
          dataRequest['dbInstanceIdentifiers'] = [
            ...dataRequest['dbInstanceIdentifiers'],
            {
              DBInstanceIdentifier: selectedInstances[i]['DBClusterMembers'][0]['DBInstanceIdentifier'],
              region: selectedInstances[i]['Region'],
              DBInstanceClass: selectedInstances[i]['DBClusterMembers'][0]['DBInstanceClass'],
              idleDays: idleDuration,
              tagValues: selectedInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
            },
          ];
        }
      }
    } else {
      dataRequest['instances'] = [];
      for (let i = 0; i < selectedInstances.length; i++) {
        dataRequest['instances'] = [
          ...dataRequest['instances'],
          {
            instanceId: selectedInstances[i]['id'],
            instanceName: selectedInstances[i]['name'],
            region: selectedInstances[i]['region'],
            instanceType: selectedInstances[i]['instanceType'],
            idleDays: idleDuration,
            tagValues: selectedInstances[i].Tags?.map((t) => ({ Key: t.Key, Value: t.Value })) || [],
          },
        ];
      }
    }

    DebugService.log('Inventory Component: Request to schedule idle initiated', dataRequest);

    let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_SCHEDULE_IDLE_SAVE;
    if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_SCHEDULE_IDLE_SAVE;

    await InventoryApiService.saveScheduleIdle(endpoint, dataRequest)
      .then((res) => {
        DebugService.log('Inventory Component: Schedule Idle response', res);
        if (res['error']) {
          setActionStatusMessage(res['error']['message'] ?? 'Something went wrong');
          setActionStatus('failed');
        } else {
          const errorList = res['data'].filter((d) => d['error'] !== undefined).map((d) => d['error']);
          if (errorList.length > 0) {
            setActionStatusMessage(errorList[0]['message'] ?? 'Something went wrong');
            setActionStatus('failed');
          } else {
            setTimeout(() => {
              setActionStatusMessage(null);
              setActionStatus(null);
              setRefresh(true);
            }, 10000);
            setOpenScheduleIdleDialog(false);
            setActionStatusMessage('Instances scheduled successfully');
            setActionStatus('success');
          }
        }
      })
      .catch((err) => {
        DebugService.error('Inventory Component: Schedule Idle failure', err);
        setActionStatusMessage('Error occurred while scheduling idle');
        setActionStatus('failed');
      })
      .finally(() => {
        setOpendialog(false);
        setShowLoading(false);
      });
  };

  const onASGCheckboxChange = (row, isChecked) => {
    setSelectedInstances((prev) => {
      if (isChecked) {
        if (prev.find((i) => i['id'] === row['id'])) return prev;
        return [...prev, row];
      } else {
        return prev.filter((i) => i['id'] !== row['id']);
      }
    });
  };

  const onDataGridCheckboxSelectHandler = (newSelectedIDList = []) => {
    let selectedInstanceList = selectedInstances;

    if (newSelectedIDList.length > 0) {
      const selectedData = rows.filter((r) => newSelectedIDList.includes(r['id']));
      selectedInstanceList = [...selectedInstanceList, ...selectedData];
      selectedInstanceList = [...new Map(selectedInstanceList.map((obj) => [obj.id, obj])).values()];
      const removedInstanceList = selectedInstanceList.filter((d) => !newSelectedIDList.includes(d['id']));
      if (removedInstanceList.length > 0) {
        selectedInstanceList = selectedInstanceList.filter((instanceObj) => !removedInstanceList.includes(instanceObj));
      }
    } else {
      selectedInstanceList = selectedInstanceList.filter((d1) => !rows.some((d2) => d2.id === d1.id));
    }

    setSelectedInstances(selectedInstanceList);
  };

  const onTakeActionHandler = (actionCase) => {
    setShowLoading(true);
    setOpendialog(false);
    setOpenModifyASGDialog(false);
    setActionStatus(null);
    setActionStatusMessage(null);
    setInstanceAction(actionCase);

    if (selectedInstances.length === 0) {
      setActionStatusMessage('Select one or more instances');
      setActionStatus('failed');
      setShowLoading(false);
    } else {
      if (actionCase === 'START_NOW') {
        const selectedStoppedInstances = selectedInstances.filter((i) => i['instanceState'] === 'stopped' || i['Status'] === 'stopped');
        if (selectedStoppedInstances.length === 0) {
          setActionStatusMessage('Select one or more stopped instances');
          setActionStatus('failed');
          setShowLoading(false);
        } else {
          setShowConfirmModal(true);
          setShowLoading(false);
        }
      } else if (actionCase === 'STOP_NOW') {
        const selectedRunningInstances = selectedInstances.filter((i) => i['instanceState'] === 'running' || i['Status'] === 'available');
        if (selectedRunningInstances.length === 0) {
          setActionStatusMessage('Select one or more running/available instances');
          setActionStatus('failed');
          setShowLoading(false);
        } else {
          setShowConfirmModal(true);
          setShowLoading(false);
        }
      } else if (actionCase === 'SCHEDULE_LATER') {
        DebugService.log('Inventory Component: Schedule later requested');
        if (type === 'asg') {
          setShowLoading(false);
          setShowConfirmModal(true);
        } else {
          const selectedRunningInstances = selectedInstances.filter((i) => i['instanceState'] === 'running' || i['Status'] === 'available');
          if (selectedRunningInstances.length === 0) {
            setActionStatusMessage('Select one or more running/available instances');
            setActionStatus('failed');
            setShowLoading(false);
          } else {
            setShowLoading(false);
            setShowConfirmModal(true);
          }
        }
      } else if (['SCALE_DOWN_ASG', 'SCALE_DEFAULT_ASG', 'SCHEDULE_IDLE'].includes(actionCase)) {
        setShowLoading(false);
        setShowConfirmModal(true);
      }
    }
  };

  const getActionMessageForConfirmModal = () => {
    switch (instanceAction) {
      case 'START_NOW': return 'started';
      case 'STOP_NOW': return 'stopped';
      case 'SCHEDULE_LATER': return 'scheduled';
      case 'SCALE_DOWN_ASG':
      case 'SCALE_DEFAULT_ASG': return 'scaled';
      case 'SCHEDULE_IDLE': return 'set Idle';
      default: return '';
    }
  };

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false);
    setShowLoading(true);

    if (isConfirm) {
      if (instanceAction === 'START_NOW') {
        const selectedStoppedInstances = selectedInstances.filter((i) => i['instanceState'] === 'stopped' || i['Status'] === 'stopped');
        instantActionHandler(InstanceActionList.START, selectedStoppedInstances);
      } else if (instanceAction === 'STOP_NOW') {
        const selectedRunningInstances = selectedInstances.filter((i) => i['instanceState'] === 'running' || i['Status'] === 'available');
        instantActionHandler(InstanceActionList.STOP, selectedRunningInstances);
      } else if (instanceAction === 'SCALE_DOWN_ASG') {
        setOpenModifyASGDialog(true);
        setShowLoading(false);
      } else if (instanceAction === 'SCALE_DEFAULT_ASG') {
        onModifyASGSave({ desireCount: null, minimumCount: null });
        setShowLoading(false);
      } else if (instanceAction === 'SCHEDULE_IDLE') {
        setOpenScheduleIdleDialog(true);
        setShowLoading(false);
      } else {
        setOpendialog(true);
        setShowLoading(false);
      }
    } else {
      onReset();
      setShowLoading(false);
    }
  };

  const onReset = () => {
    setActionStatus(null);
    setActionStatusMessage(null);
    setSelectedInstances([]);
    setShowConfirmModal(false);
    setActionBy('DROPDOWN');
    setInstanceAction(null);
    setFilterData({ field: '', value: '', tagField: '', tagValue: [] });
  };

  const onRefreshClick = () => {
    setFilterData({ field: '', value: '', tagField: '', tagValue: [] });
    setActionStatus(null);
    setActionStatusMessage(null);
    setRefresh(true);
  };

  const activateSubscription = useCallback(() => {
    setActionStatusMessage(null);
    setActionStatus(null);

    const userSubscriptionId = userDetails?.['userSubscriptionId'];
    if (userSubscriptionId) {
      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
      const parseToJson = JSON.parse(userDetails);
      const requestBody = {
        userId: parseToJson?.['userId'],
        accountId: selectedAccount,
        userSubscriptionId: userSubscriptionId,
      };

      DebugService.log('Optimization Component: Activate subscription request initiated', requestBody);

      SubscriptionApiService.activateSubscription(requestBody)
        .then((res) => {
          DebugService.log('Optimization Component: Activate subscription response', res);
          if (res['data']) {
            setActionStatus('success');
            setActionStatusMessage(`${res['data']['message']}. Reload your page`);
            refetchUserDetails();
          } else {
            if (res['error']['code'] === 525) {
              navigate(CONST_ROUTE_PATH.PRICING);
            } else {
              setActionStatus('failed');
              setActionStatusMessage(res['error']['message'] ?? 'Subscription activation failed');
            }
          }
        })
        .catch((error) => {
          DebugService.log('Optimization Component: Plan purchase failed', error);
          setActionStatus('failed');
          setActionStatusMessage('Something went wrong');
        });
    }
  }, [userDetails]);

  const activateNowEl = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', marginLeft: '1vw' }}>
      <Typography color={'#33adff'} style={{ fontSize: '14px' }}>
        See only {TRIAL_SUBSCRIPTION_DATA_LIMIT} records?&nbsp;
        <Button
          variant="contained"
          size="small"
          title="Click to activate"
          onClick={() => setShowActivateNowConfirmModal(true)}
          style={{ textTransform: 'none', padding: '3px', lineHeight: '1.2', backgroundColor: '#ff6600' }}
        >
          Activate Now
        </Button>
        &nbsp;to access full data.
      </Typography>
    </Box>
  );

  const activateNowConfirmationHandler = (isConfirm) => {
    if (isConfirm) activateSubscription();
    setShowActivateNowConfirmModal(false);
  };

  const handleGridColumnAction = (actionCase, instanceID) => {
    if (instanceID) {
      setActionBy('GRIDCELL');
      setInstanceAction(actionCase);
      onDataGridCheckboxSelectHandler([instanceID]);
    }
  };

  useEffect(() => {
    if (actionBy === 'GRIDCELL' && selectedInstances.length > 0 && instanceAction !== null) {
      onTakeActionHandler(instanceAction);
    }
  }, [selectedInstances, actionBy, instanceAction, rows]);

  const onFieldChange = useCallback((fieldName, value) => {
    if (fieldName === 'field') {
      setFilterData((prev) => {
        if (prev.field !== '' || prev.value !== '' || (prev.tagValue?.length ?? 0) > 0) {
          refetchAfterFieldChange.current = true;
        }
        return { field: value, value: '', tagField: '', tagValue: [] };
      });
    } else if (fieldName === 'tagField') {
      setFilterData((prev) => ({ ...prev, [fieldName]: value, tagValue: [] }));
    } else {
      setFilterData((prev) => ({ ...prev, [fieldName]: value }));
    }
  }, []);

  const onFilterEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchData();
    }
  };

  useEffect(() => {
    if (filterData['field'] === 'tags' && filterData.tagValue.length > 0) {
      fetchData();
    }
  }, [filterData]);

  useEffect(() => {
    if (refetchAfterFieldChange.current) {
      refetchAfterFieldChange.current = false;
      fetchData();
    }
  }, [filterData.field]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, totalInstancesApiData);
  }, [totalInstancesApiData]);

  const isTrial =
    [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(userDetails['subscriptionStatus']) ||
    [SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS));

  return (
    <div>
      <>
          {/* KPI Tiles */}
          <div className="kpi-row cols-3">
            <div className="kpi t1">
              <div className="ico" style={{ color: 'var(--blue)' }}>
                <InstanceIcon />
              </div>
              <div>
                <div className="lbl">{type === 'asg' ? 'Total ASG' : 'Total Instances'}</div>
                <div className="val">{totalInstancesCount}</div>
              </div>
            </div>
            <div className="kpi t2">
              <div className="ico" style={{ color: 'var(--green)' }}>
                <InstanceIcon />
              </div>
              <div>
                <div className="lbl">Total Running Instances</div>
                <div className="val">{totalRunningInstancesCount}</div>
              </div>
            </div>
            <div className="kpi t3">
              <div className="ico" style={{ color: 'var(--red)' }}>
                <InstanceIcon />
              </div>
              <div>
                <div className="lbl">{type === 'asg' ? 'Total Unused ASG' : 'Total Stopped Instances'}</div>
                <div className="val">{totalStoppedInstancesCount}</div>
              </div>
            </div>
          </div>

          {/* Filter & Action Bar */}
          <div className="filter-bar" style={{ borderRadius: '8px', marginBottom: '14px' }}>
            {['ec2', 'asg', 'rds'].includes(type) && (
              <>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Filter Field</InputLabel>
                  <Select
                    value={filterData['field']}
                    label="Filter Field"
                    onChange={(e) => { e.preventDefault(); onFieldChange('field', e.target.value); }}
                  >
                    {type === 'ec2'
                      ? ec2FilterFieldList.map((f, i) => <MenuItem key={i} value={f.value}>{f.label}</MenuItem>)
                      : type === 'asg'
                      ? asgFilterFieldList.map((f, i) => <MenuItem key={i} value={f.value}>{f.label}</MenuItem>)
                      : rdsFilterFieldList.map((f, i) => <MenuItem key={i} value={f.value}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>

                {filterData['field'] !== 'tags' && (
                  <TextField
                    title="Enter search value and hit enter"
                    placeholder="Enter value and hit Enter"
                    label="Search Value"
                    variant="outlined"
                    size="small"
                    id="fieldvalue"
                    name="fieldvalue"
                    value={filterData['value']}
                    onChange={(e) => { e.preventDefault(); onFieldChange('value', e.target.value); }}
                    onKeyDown={(e) => onFilterEnter(e)}
                  />
                )}

                {filterData['field'] === 'tags' && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Select Tag</InputLabel>
                      <Select
                        value={filterData['tagField']}
                        label="Select Tag"
                        onChange={(e) => { e.preventDefault(); onFieldChange('tagField', e.target.value); }}
                      >
                        {filterTagList.map((f, i) => <MenuItem key={i} value={f}>{f['Key']}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Select Tag Value</InputLabel>
                      <Select
                        value={filterData['tagValue'] ?? []}
                        label="Select Tag Value"
                        multiple={true}
                        onChange={(e) => { e.preventDefault(); onFieldChange('tagValue', e.target.value); }}
                      >
                        {filterData['tagField'] !== '' &&
                          filterData['tagField']?.['Value']?.length > 0 &&
                          filterData['tagField']['Value'].map((v, i) => <MenuItem key={i} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </>
                )}
              </>
            )}

            <div className="spacer" />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value="SELECT_ACTION"
                onChange={(e) => { e.preventDefault(); onTakeActionHandler(e.target.value); }}
                disabled={selectedInstances.length === 0 || currentUserAccess === USER_ROLE.READ_ONLY}
              >
                <MenuItem value="SELECT_ACTION">Select Action</MenuItem>
                {['rds', 'ec2'].includes(type) && <MenuItem value="START_NOW">Start Now</MenuItem>}
                {['rds', 'ec2'].includes(type) && <MenuItem value="STOP_NOW">Stop Now</MenuItem>}
                {['asg'].includes(type) && <MenuItem value="SCALE_DOWN_ASG">Scale Down</MenuItem>}
                {['asg'].includes(type) && <MenuItem value="SCALE_DEFAULT_ASG">Scale Default</MenuItem>}
                {['ec2', 'rds'].includes(type) && <MenuItem value="SCHEDULE_IDLE">Schedule Idle Shutdown</MenuItem>}
                <MenuItem value="SCHEDULE_LATER">Schedule {['asg'].includes(type) ? 'Auto-Scaling' : 'Shutdown'}</MenuItem>
              </Select>
            </FormControl>

            <button
              className="btn primary"
              onClick={() => onRefreshClick()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            >
              Refresh <HiOutlineRefresh />
            </button>
          </div>

          {/* Table Card */}
          <div className="card" style={{ borderRadius: '12px' }}>
            {type === 'asg' ? (
              <CustomTable
                rows={rows}
                exportFilename={buildExportFilename('asg_inventory', selectedAccount)}
                columns={[
                  checkboxCol,
                  { key: 'id', label: 'Name' },
                  { key: 'Region', label: 'Region' },
                  { key: 'MinSize', label: 'Min Size' },
                  { key: 'MaxSize', label: 'Max Size' },
                  { key: 'DesiredCapacity', label: 'Desired Capacity' },
                  {
                    key: 'instances',
                    label: 'Instances',
                    render: (row) => {
                      const instances = row.Instances ?? [];
                      if (instances.length === 0) return <span>—</span>;
                      return (
                        <Button
                          variant="text"
                          size="small"
                          style={{ textTransform: 'none', padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAsgInstancesClick(e, instances);
                          }}
                        >
                          {instances.length} Instances
                        </Button>
                      );
                    }
                  }
                ]}
                loading={showLoading}
                subscriptionGated
              />
            ) : (
              <CustomTable
                rows={rows}
                columns={[
                  checkboxCol,
                  ...mapGridColumnsToTableColumns(dataColumns)
                ]}
                loading={showLoading}
                exportFilename={buildExportFilename(`${type}_inventory`, selectedAccount)}
                subscriptionGated
              />
            )}
          </div>

          {/* Modals */}
          {showActivateNowConfirmModal && (
            <ConfirmModal
              open={true}
              headingLabel="Activate Your Subscription!"
              message={[
                <p key="m1">By clicking the button below, you'll gain full access to premium features, exclusive content, and personalized recommendations.</p>,
                <br key="m2" />,
              ]}
              continueBtnLabel="Proceed"
              cancelBtnLabel="Close"
              onActionTaken={(isConfirm) => activateNowConfirmationHandler(isConfirm)}
            />
          )}

          {Opendialog && (
            <InstanceScheduler
              open={true}
              handleClose={() => setOpendialog(false)}
              onSave={onScheduleLaterSave}
              instanceData={selectedInstances}
            />
          )}

          {openModifyASGDialog && (
            <ModifyASG
              open={true}
              handleClose={() => setOpenModifyASGDialog(false)}
              onSave={onModifyASGSave}
              instanceData={selectedInstances}
            />
          )}

          {openScheduleIdleDialog && (
            <ScheduleIdle
              open={true}
              handleClose={() => setOpenScheduleIdleDialog(false)}
              onSave={onScheduleIdleSave}
              instanceData={selectedInstances}
            />
          )}

          <Dialog
            open={openAsgInstances}
            onClose={() => setAsgInstancesAnchorEl(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e9ecf2' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
                Instance Details
                <Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary', fontWeight: 400 }}>
                  ({asgInstancesData.length} instance{asgInstancesData.length !== 1 ? 's' : ''})
                </Typography>
              </Typography>
              <IconButton size="small" onClick={() => setAsgInstancesAnchorEl(null)}>
                ✕
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, pb: 2 }}>
              <MuiTableContainer sx={{ px: 0 }}>
                <MuiTable size="medium">
                  <MuiTableHead>
                    <MuiTableRow sx={{ '& th': { background: '#4a5a72', color: '#fff', fontWeight: 600, fontSize: 13, py: 1.5, px: 3 } }}>
                      <MuiTableCell>Instance ID</MuiTableCell>
                      <MuiTableCell>Health</MuiTableCell>
                      <MuiTableCell>Lifecycle</MuiTableCell>
                      <MuiTableCell>Type</MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {asgInstancesData.map((inst, i) => (
                      <MuiTableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { background: '#f0f6ff' }, '& td': { px: 3, py: 1.5 } }}>
                        <MuiTableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: '#2f80c4' }}>
                          {inst.InstanceId ?? inst.instanceId}
                        </MuiTableCell>
                        <MuiTableCell>{inst.HealthStatus ?? inst.healthStatus ?? '—'}</MuiTableCell>
                        <MuiTableCell>{inst.LifecycleState ?? inst.lifecycleState ?? '—'}</MuiTableCell>
                        <MuiTableCell>{inst.InstanceType ?? inst.instanceType ?? '—'}</MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            </DialogContent>
          </Dialog>

          {actionStatus !== null && (
            <SnackbarNotification
              initialOpen={true}
              duration={7000}
              message={actionStatusMessage}
              actionButtonName="Ok"
              theme={actionStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
              yPosition="top"
              xPosition="center"
            />
          )}

          {showConfirmModal && (
            <ConfirmModal
              open={true}
              message={[
                <Typography key="cm1" variant="subtitle2" color="#000000">
                  Following instances will be {getActionMessageForConfirmModal()}
                </Typography>,
                <Divider key="cm2" className="divider-custom" />,
                ...selectedInstances.map((instance) => (
                  <Typography key={instance['id']} variant="body2">
                    {type === 'rds' ? instance['DBInstanceIdentifierList'] : instance['id']}
                    &nbsp;
                    {instance['name'] ? `(${instance['name']})` : ''}
                  </Typography>
                )),
              ]}
              onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
            />
          )}
      </>
    </div>
  );
}

export function ScheduledEvents() {
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedIDList, setSelectedIDList] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarCase, setSnackbarCase] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDeleteRecurring, setIsDeleteRecurring] = useState(false);
  const [currentUserAccess, setCurrentUserAccess] = useState(null);
  const [scheduledEventsApiData, setScheduledEventsApiData] = useState(null);

  const location = useLocation();
  const { type } = useCurrentInstanceType();
  const { selectedAccount } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();

  const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const allSelected = data.length > 0 && data.every(r => selectedIDList.includes(r.id));
  
  const toggleAllSelected = () => {
    if (allSelected) {
      setSelectedIDList([]);
    } else {
      setSelectedIDList(data.map(r => r.id));
    }
  };

  const toggleRowSelected = (rowId) => {
    setSelectedIDList(prev => {
      const exists = prev.includes(rowId);
      if (exists) {
        return prev.filter(id => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const checkboxCol = {
    key: 'checkbox',
    label: (
      <span
        className={`checkbox ${allSelected ? 'checked' : ''}`}
        onClick={toggleAllSelected}
      />
    ),
    className: 'center',
    render: (row) => (
      <span
        className={`checkbox ${selectedIDList.includes(row.id) ? 'checked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleRowSelected(row.id);
        }}
      />
    ),
  };

  const onDataGridCheckboxSelectHandler = (selectedIDListVal = []) => {
    DebugService.log('Upcoming Schedules Component: selected instance id list', selectedIDListVal);
    setSelectedIDList(selectedIDListVal);
  };

  const onDeleteClick = () => {
    setShowLoader(true);
    setSnackbarMessage(null);
    setSnackbarCase(null);

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_DELETE_SCHEDULED_EVENTS;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_DELETE_SCHEDULED_EVENTS;
      if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_DELETE_SCHEDULED_EVENTS;

      const dataRequest = { userAccountId: selectedAccount ?? '' };
      if (isDeleteRecurring) {
        dataRequest['scheduleGroupIds'] = data.filter((d) => selectedIDList.includes(d['id'])).map((d) => d['scheduleGroupId']);
      } else {
        dataRequest['ids'] = selectedIDList;
      }

      DebugService.log(`Upcoming Schedules Component: Delete request with recurring option as ${isDeleteRecurring} is initiated`, dataRequest);

      InventoryApiService.deleteScheduledEvents(endpoint, dataRequest)
        .then((res) => {
          DebugService.log('Upcoming Schedules Component: Data', res);
          if (res['data']) {
            setSnackbarCase('success');
            setSnackbarMessage('Scheduled instances deleted successfully');
            setTimeout(() => fetchData(), 1000);
          }
          if (res['error']) {
            setSnackbarCase('failed');
            setSnackbarMessage(res['error']?.['message']);
          }
          setShowLoader(false);
        })
        .catch((err) => {
          DebugService.error('Upcoming Schedules Component: Error caught while deleting data', err);
          setSnackbarCase('error');
          setSnackbarMessage("Couldn't delete Scheduled instances due to some error");
          setShowLoader(false);
        });
    }
  };

  const processData = (data) =>
    data.map((d) => {
      let startDateTime = d['startTime'];
      let stopDateTime = d['stopTime'];

      if (selectedTimezone?.['value'] && d['startTime']) {
        startDateTime = new Date(d['startTime']).toLocaleString('en-US', { timeZone: selectedTimezone['value'] });
      }
      if (selectedTimezone?.['value'] && d['stopTime']) {
        stopDateTime = new Date(d['stopTime']).toLocaleString('en-US', { timeZone: selectedTimezone['value'] });
      }

      return {
        ...d,
        startTime: format(startDateTime, 'dd-MM-yyyy HH:mm:ss'),
        stopTime: format(stopDateTime, 'dd-MM-yyyy HH:mm:ss'),
      };
    });

  const fetchData = async () => {
    setShowLoader(true);
    try {
      setData([]);
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_SCHEDULED_EVENTS_LIST;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_SCHEDULED_EVENTS_LIST;
      if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_SCHEDULED_EVENTS_LIST;

      InventoryApiService.getScheduledEvents(selectedAccount, endpoint)
        .then((res) => {
          DebugService.log('Upcoming Schedules Component: Data', res);
          setScheduledEventsApiData(res?.data ? res : null);
          setRawData(res['data'] || []);
          setData(processData(res['data'] ?? []));
          setShowLoader(false);
        })
        .catch((err) => {
          setShowLoader(false);
          DebugService.error('Upcoming Schedules Component: Error caught while fetching data', err);
        });
    } catch (error) {
      setShowLoader(false);
      DebugService.error('Upcoming Schedules Component: Fetch data error', error);
    }
  };

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false);
    if (isConfirm) onDeleteClick();
  };

  useEffect(() => {
    fetchData();
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);
    setCurrentUserAccess(parseToJson['roleName']);
  }, [location.pathname, selectedAccount]);

  useEffect(() => {
    setShowLoader(true);
    setData(processData(rawData ?? []));
    setShowLoader(false);
  }, [selectedTimezone]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, scheduledEventsApiData);
  }, [scheduledEventsApiData]);

  return (
    <div style={{ paddingTop: '16px' }}>
      <>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <TimezoneSelect value={selectedTimezone} onChange={(v) => setSelectedTimezone(v)} />
            </div>
            <LoadingButton
              variant="outlined"
              title="Delete"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => setShowConfirmModal(true)}
              loading={showLoader}
              loadingPosition="end"
              endIcon={<RiDeleteBinLine />}
              style={{ textTransform: 'none' }}
            >
              Delete
            </LoadingButton>
          </div>

          <div className="card" style={{ borderRadius: '12px' }}>
            <CustomTable
              rows={data}
              columns={[
                checkboxCol,
                ...mapGridColumnsToTableColumns(type === 'ec2' ? ec2ColumnsForScheduledEvents : rdsColumnsForScheduledEvents)
              ]}
              loading={showLoader}
              exportFilename={buildExportFilename(`scheduled_events_${type}`, selectedAccount)}
              subscriptionGated
            />
          </div>

          {snackbarMessage !== null && (
            <SnackbarNotification
              initialOpen={true}
              duration={7000}
              message={snackbarMessage}
              actionButtonName="Ok"
              theme={snackbarCase === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
              yPosition="top"
              xPosition="center"
            />
          )}

          {showConfirmModal && (
            <ConfirmModal
              open={true}
              message={[
                <div key="p1">Proceed with selected events only or include recurring ones?</div>,
                <div key="p2">
                  <Checkbox onChange={(e) => setIsDeleteRecurring(e.target.checked)} />
                  Include recurring events
                </div>,
                <br key="p3" />,
              ]}
              onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
            />
          )}
      </>
    </div>
  );
}

export function IdleEventsComponent() {
  const [data, setData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedIDList, setSelectedIDList] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarCase, setSnackbarCase] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentUserAccess, setCurrentUserAccess] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [openScheduleIdleDialog, setOpenScheduleIdleDialog] = useState(false);
  const [idleEventsApiData, setIdleEventsApiData] = useState(null);

  const location = useLocation();
  const { type } = useCurrentInstanceType();
  const { selectedAccount } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();

  const dataColumns = type === 'ec2' ? ec2ColumnsForIdleEvents : rdsColumnsForIdleEvents;

  const allSelected = data.length > 0 && data.every(r => selectedIDList.includes(r.id));
  
  const toggleAllSelected = () => {
    if (allSelected) {
      setSelectedIDList([]);
    } else {
      setSelectedIDList(data.map(r => r.id));
    }
  };

  const toggleRowSelected = (rowId) => {
    setSelectedIDList(prev => {
      const exists = prev.includes(rowId);
      if (exists) {
        return prev.filter(id => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const checkboxCol = {
    key: 'checkbox',
    label: (
      <span
        className={`checkbox ${allSelected ? 'checked' : ''}`}
        onClick={toggleAllSelected}
      />
    ),
    className: 'center',
    render: (row) => (
      <span
        className={`checkbox ${selectedIDList.includes(row.id) ? 'checked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleRowSelected(row.id);
        }}
      />
    ),
  };

  const onDataGridCheckboxSelectHandler = (selectedIDListVal = []) => {
    DebugService.log('Idle Events Component: selected instance id list', selectedIDListVal);
    setSelectedIDList(selectedIDListVal);
  };

  const onDeleteClick = () => {
    setShowLoader(true);
    setSnackbarMessage(null);
    setSnackbarCase(null);

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_SCHEDULE_IDLE_DELETE;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_SCHEDULE_IDLE_DELETE;

      const dataRequest = { userAccountId: selectedAccount ?? '', idleScheduleIds: selectedIDList };

      DebugService.log('Idle Events Component: Delete request is initiated', dataRequest);

      InventoryApiService.deleteIdleEvents(endpoint, dataRequest)
        .then((res) => {
          DebugService.log('Idle Events Component: Data', res);
          if (res['data']) {
            setSnackbarCase('success');
            setSnackbarMessage('Scheduled instances deleted successfully');
            setTimeout(() => fetchData(), 1000);
          }
          if (res['error']) {
            setSnackbarCase('failed');
            setSnackbarMessage(res['error']?.['message']);
          }
          setShowLoader(false);
        })
        .catch((err) => {
          DebugService.error('Idle Events Component: Error caught while deleting data', err);
          setSnackbarCase('error');
          setSnackbarMessage("Couldn't delete Scheduled instances due to some error");
          setShowLoader(false);
        });
    }
  };

  const onEditClick = ({ idleDuration }) => {
    setShowLoader(true);
    setSnackbarMessage(null);
    setSnackbarCase(null);

    if (selectedIDList.length > 0) {
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_SCHEDULE_IDLE_UPDATE;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_SCHEDULE_IDLE_UPDATE;

      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
      const parseToJson = JSON.parse(userDetails);
      const dataRequest = { userAccountId: selectedAccount ?? '', userId: '' };

      if (type === 'rds') {
        dataRequest['dbInstances'] = data
          .filter((d) => selectedIDList.includes(d['id']))
          .map((d) => ({ instanceId: d['InstanceId'], idleScheduleId: d['id'], idleDays: idleDuration, region: d['Region'] }));
      } else {
        dataRequest['instances'] = data
          .filter((d) => selectedIDList.includes(d['id']))
          .map((d) => ({
            instanceId: d['InstanceId'],
            idleScheduleId: d['id'],
            idleDays: idleDuration,
            region: d['Region'],
            instanceType: d['InstanceType'],
            instanceName: d['InstanceName'],
          }));
      }

      if (parseToJson['userId']) dataRequest['userId'] = parseToJson['userId'];

      DebugService.log('Idle Events Component: Edit request is initiated', dataRequest);

      InventoryApiService.updateScheduleIdle(endpoint, dataRequest)
        .then((res) => {
          DebugService.log('Idle Events Component: Data', res);
          if (res['data']) {
            setOpenScheduleIdleDialog(false);
            setSnackbarCase('success');
            setSnackbarMessage('Scheduled instances updated successfully');
            setTimeout(() => fetchData(), 1000);
          }
          if (res['error']) {
            setSnackbarCase('failed');
            setSnackbarMessage(res['error']?.['message']);
          }
          setShowLoader(false);
        })
        .catch((err) => {
          DebugService.error('Idle Events Component: Error caught while editing data', err);
          setSnackbarCase('error');
          setSnackbarMessage("Couldn't edit Scheduled instances due to some error");
          setShowLoader(false);
        });
    }
  };

  const fetchData = async () => {
    setShowLoader(true);
    try {
      setData([]);
      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_IDLE_EVENT_LIST;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_IDLE_EVENT_LIST;

      InventoryApiService.getIdleEvents(selectedAccount, endpoint)
        .then((res) => {
          DebugService.log('Idle Events Component: Data', res);
          setIdleEventsApiData(res?.data ? res : null);
          setData((res['data'] ?? []).map((d) => ({ ...d, id: d['IdleScheduleId'] })));
          setShowLoader(false);
        })
        .catch((err) => {
          setShowLoader(false);
          DebugService.error('Idle Events Component: Error caught while fetching data', err);
        });
    } catch (error) {
      setShowLoader(false);
      DebugService.error('Idle Events Component: Fetch data error', error);
    }
  };

  const onConfirmModalAction = (isConfirm) => {
    setShowConfirmModal(false);
    if (isConfirm) {
      if (selectedAction === 'DELETE') onDeleteClick();
      else if (selectedAction === 'EDIT') setOpenScheduleIdleDialog(true);
    }
  };

  const onSelectAction = (action) => {
    setSelectedAction(action);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    fetchData();
    const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    const parseToJson = JSON.parse(userDetails);
    setCurrentUserAccess(parseToJson['roleName']);
  }, [location.pathname, selectedAccount]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, idleEventsApiData);
  }, [idleEventsApiData]);

  return (
    <div style={{ paddingTop: '16px' }}>
      <>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <LoadingButton
              variant="outlined"
              title="Delete"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => onSelectAction('DELETE')}
              loading={showLoader}
              loadingPosition="end"
              endIcon={<RiDeleteBinLine />}
              style={{ textTransform: 'none' }}
            >
              Delete
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              title="Edit"
              disabled={!selectedIDList.length || currentUserAccess === USER_ROLE.READ_ONLY}
              onClick={() => onSelectAction('EDIT')}
              loading={showLoader}
              loadingPosition="end"
              endIcon={<FaRegEdit />}
              style={{ textTransform: 'none' }}
            >
              Edit
            </LoadingButton>
          </div>

          <div className="card" style={{ borderRadius: '12px' }}>
            <CustomTable
              rows={data}
              columns={[
                checkboxCol,
                ...mapGridColumnsToTableColumns(dataColumns)
              ]}
              loading={showLoader}
              exportFilename={buildExportFilename(`idle_events_${type}`, selectedAccount)}
              subscriptionGated
            />
          </div>

          {openScheduleIdleDialog && (
            <ScheduleIdle
              open={true}
              handleClose={() => setOpenScheduleIdleDialog(false)}
              onSave={onEditClick}
              instanceData={data.filter((d) => selectedIDList.includes(d['id'])) ?? []}
            />
          )}

          {snackbarMessage !== null && (
            <SnackbarNotification
              initialOpen={true}
              duration={7000}
              message={snackbarMessage}
              actionButtonName="Ok"
              theme={snackbarCase === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
              yPosition="top"
              xPosition="center"
            />
          )}

          {showConfirmModal && (
            <ConfirmModal
              open={true}
              message={[
                <div key="cm1">Do you want to {selectedAction?.toLocaleLowerCase()} selected instances?</div>,
                <br key="cm2" />,
              ]}
              onActionTaken={(isConfirm) => onConfirmModalAction(isConfirm)}
            />
          )}
      </>
    </div>
  );
}

export function ActivityLogComponent() {
  const [rawData, setRawData] = useState([]);
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbarMessage, setShowSnackbarMessage] = useState('HIDE');
  const [asgCostAnchorEl, setAsgCostAnchorEl] = React.useState(null);
  const [popupData, setPopupData] = React.useState(null);
  const [activityInventory, setActivityInventory] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const { getScopedItem } = useScopedLocalStorage();
  const open = Boolean(asgCostAnchorEl);
  const id = open ? 'simple-popover' : undefined;
  const location = useLocation();
  const { type } = useCurrentInstanceType();
  const { selectedAccount } = useAuth();

  const dataColumns =
    type === 'rds' ? rdsColumnsForActivityevents : type === 'asg' ? asgActivityLogColumns : ec2ActivityLogColumnsForDataGrid;

  const asgCostClickedComponent = (e, data) => {
    if (data?.['costingDetails']?.['instanceCostingDetails']?.length) {
      setAsgCostAnchorEl(e.currentTarget);
      setPopupData(data['costingDetails']);
    }
  };

  const processData = (data) =>
    data.map((d, i) => {
      let startDateTime = d['startTime'];
      let stopDateTime = d['endTime'];

      if (selectedTimezone?.['value'] && d['startTime']) {
        startDateTime = new Date(d['startTime']).toLocaleString('en-US', { timeZone: selectedTimezone['value'] });
      }
      if (selectedTimezone?.['value'] && d['endTime']) {
        stopDateTime = new Date(d['endTime']).toLocaleString('en-US', { timeZone: selectedTimezone['value'] });
      }

      const result = { ...d, id: i + 1 };
      if (startDateTime) result['startTime'] = format(startDateTime, 'dd-MM-yyyy HH:mm:ss');
      if (stopDateTime) result['endTime'] = format(stopDateTime, 'dd-MM-yyyy HH:mm:ss');
      if (type === 'asg') result['asgCostClickedComponent'] = asgCostClickedComponent;

      return result;
    });

  useEffect(() => {
    if (selectedDate !== null) {
      setData([]);
      setIsLoading(true);
      setShowSnackbarMessage('HIDE');

      const dataRequest = {
        userAccountId: selectedAccount ?? '',
        fromDate: selectedDate.startOf('day'),
        toDate: selectedDate.endOf('day'),
      };

      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_ACTIVTY_EVENTS;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_ACTIVTY_EVENTS;
      if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_ACTIVTY_EVENTS;

      InventoryApiService.getActivityInventory(endpoint, dataRequest)
        .then((res) => {
          DebugService.log(`Activity Log Component: Data for date ${selectedDate}`, res);
          if (res['error']) setShowSnackbarMessage('SHOW');
          setActivityInventory(res?.data ? res : null);
          setRawData(res['data'] ?? []);
          setData(processData(res['data'] ?? []));
          setIsLoading(false);
        })
        .catch((err) => {
          DebugService.log(`Activity Log Component: Error caught while fetching data for date ${selectedDate}`, err);
          setIsLoading(false);
        });
    }
  }, [selectedDate, location.pathname, selectedAccount]);

  useEffect(() => {
    setIsLoading(true);
    setData(processData(rawData ?? []));
    setIsLoading(false);
  }, [selectedTimezone]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, activityInventory);
  }, [activityInventory]);

  return (
    <div style={{ paddingTop: '16px' }}>
      <>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <TimezoneSelect value={selectedTimezone} onChange={(v) => setSelectedTimezone(v)} />
            </div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  slots={{ textField: (props) => <TextField {...props} variant="outlined" margin="normal" fullWidth /> }}
                  format="DD/MM/YYYY"
                  onClose={() => {}}
                  maxDate={dayjs()}
                  disableFuture={true}
                />
              </DemoContainer>
            </LocalizationProvider>
          </div>

          <div className="card" style={{ borderRadius: '12px' }}>
            <Popover
              id={id}
              open={open}
              anchorEl={asgCostAnchorEl}
              onClose={() => setAsgCostAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {popupData !== null && (
                <Box className="popup-section">
                  <MuiTableContainer component={Paper}>
                    <MuiTable sx={{ minWidth: 100 }} size="small" aria-label="a dense table">
                      <MuiTableHead>
                        <MuiTableRow>
                          <MuiTableCell>Type</MuiTableCell>
                          <MuiTableCell>Cost</MuiTableCell>
                          <MuiTableCell>Duration</MuiTableCell>
                        </MuiTableRow>
                      </MuiTableHead>
                      <MuiTableBody>
                        {popupData['instanceCostingDetails']?.length > 0 &&
                          popupData['instanceCostingDetails'].map((costDetails, i) => (
                            <MuiTableRow key={i}>
                              <MuiTableCell>{costDetails['instanceType']}</MuiTableCell>
                              <MuiTableCell>{costDetails['cost']}</MuiTableCell>
                              <MuiTableCell>{costDetails['duration']}</MuiTableCell>
                            </MuiTableRow>
                          ))}
                      </MuiTableBody>
                    </MuiTable>
                  </MuiTableContainer>
                </Box>
              )}
            </Popover>

            <CustomTable
              rows={data}
              columns={mapGridColumnsToTableColumns(dataColumns)}
              loading={isLoading}
              exportFilename={buildExportFilename(`activity_log_${type}`, selectedAccount)}
              subscriptionGated
            />
          </div>

          {showSnackbarMessage === 'SHOW' && (
            <SnackbarNotification
              initialOpen={true}
              duration={7000}
              message="No activity found for the selected date"
              actionButtonName="Ok"
              theme={SNACKBAR_THEME.RED}
              yPosition="top"
              xPosition="center"
            />
          )}
      </>
    </div>
  );
}

export function ModificationLogComponent() {
  const [fromDate, setFromDate] = useState(dayjs(new Date(new Date().setDate(new Date().getDate() - 2))));
  const [toDate, setToDate] = useState(dayjs(new Date(yesterdayDate())));
  const [changesInstances, setChangesInstances] = useState([]);
  const [addedInstances, setAddedInstances] = useState([]);
  const [removedInstances, setRemovedInstances] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showSnackbarMessage, setShowSnackbarMessage] = useState('HIDE');
  const [selectedTab, setSelectedTab] = useState(INVENTORY_EVENTS.MODIFICATION_CHANGES);
  const [modifiedEventsApiData, setModifiedEventsApiData] = useState(null);

  const location = useLocation();
  const { type } = useCurrentInstanceType();
  const { selectedAccount } = useAuth();

  const changesColumns =
    type === 'rds' ? rdsModificationChangesColumns : type === 'asg' ? asgModificationLogColumnsForChanges : ec2ModificationChangesColumns;

  const addedColumns =
    type === 'rds' ? rdsColumnsForModificationEvents : type === 'asg' ? asgModificationLogColumns : ec2ModificationLogColumnsForDataGrid;

  const getLogs = () => {
    if (fromDate && toDate) {
      setShowLoading(true);
      setShowSnackbarMessage('HIDE');

      const dateRequestBody = {
        fromDate: encodeURIComponent(fromDate.toISOString()),
        toDate: encodeURIComponent(toDate.toISOString()),
      };

      let endpoint = APIConstants.ENDPOINT_INVENTORY_EC2_CHANGES;
      if (type === 'rds') endpoint = APIConstants.ENDPOINT_INVENTORY_RDS_CHANGES;
      if (type === 'asg') endpoint = APIConstants.ENDPOINT_INVENTORY_ASG_CHANGES;

      InventoryApiService.getModifiedEvents(selectedAccount, endpoint, dateRequestBody)
        .then((res) => {
          DebugService.log('Modification Log Component: API Response', res);
          setModifiedEventsApiData(res?.data ? res : null);

          const data = res['data'] ?? null;
          let addedData = [];
          let removedData = [];

          if (!res['data']) setShowSnackbarMessage('SHOW');

          const getSafeId = (item) =>
            item.id ||
            item.InstanceId ||
            item.DBInstanceIdentifier ||
            item.AutoScalingGroupName ||
            item.name ||
            `row-${Math.random().toString(36).substr(2, 9)}`;

          if (data && data['addedInstances']) {
            if (type === 'rds') {
              addedData = data['addedInstances'].map((instance, i) => ({ ...instance, id: i + 1 }));
            } else if (type === 'asg') {
              addedData = data['addedInstances'].map((instance) => ({
                ...instance,
                id: getSafeId(instance),
                name: instance['AutoScalingGroupName'],
              }));
            } else {
              addedData = data['addedInstances'].map((instance) => ({
                ...instance,
                id: instance.Instance_ID || instance.InstanceId || getSafeId(instance),
                name: instance.name,
                instanceType: instance.Instance_type,
                availabilityZone: instance.Availability_zone,
                publicIPv4DNS: instance.Public_IPv4_DNS,
                publicIPv4Address: instance.Public_IPv4_address,
                iPv6IPs: instance?.IPv6_IPs,
                monitoring: instance.Monitoring,
                securityGroupName: instance.Security_group_name,
                keyName: instance.Key_name,
                launchTime: format(instance.Launch_Time, 'dd-MM-yyyy HH:mm:ss'),
                platfromDetails: instance.Platform_details,
              }));
            }
            setAddedInstances(addedData);
          }

          if (data && data['removedInstances']) {
            if (type === 'rds') {
              removedData = data['removedInstances'].map((instance, i) => ({ ...instance, id: i + 1 }));
            } else if (type === 'asg') {
              removedData = data['removedInstances'].map((instance) => ({
                ...instance,
                id: getSafeId(instance),
                name: instance['AutoScalingGroupName'],
              }));
            } else {
              removedData = data['removedInstances'].map((instance) => ({
                ...instance,
                id: instance.Instance_ID || instance.InstanceId || getSafeId(instance),
                name: instance.name,
                instanceType: instance.Instance_type,
                availabilityZone: instance.Availability_zone,
                publicIPv4DNS: instance.Public_IPv4_DNS,
                publicIPv4Address: instance.Public_IPv4_address,
                iPv6IPs: instance?.IPv6_IPs,
                monitoring: instance.Monitoring,
                securityGroupName: instance.Security_group_name,
                keyName: instance.Key_name,
                launchTime: format(instance.Launch_Time, 'dd-MM-yyyy HH:mm:ss'),
                platfromDetails: instance.Platform_details,
              }));
            }
            setRemovedInstances(removedData);
          }

          let changesInstances = [];
          addedData?.forEach((newInstance) => {
            const matchingOldInstance = removedData?.find(
              (oldInstance) =>
                oldInstance['id'] === newInstance['id'] ||
                oldInstance['name'] === newInstance['name'] ||
                oldInstance['AutoScalingGroupName'] === newInstance['AutoScalingGroupName']
            );

            if (matchingOldInstance) {
              const updatedInstance = JSON.parse(JSON.stringify(newInstance));
              const safeId = getSafeId(newInstance);

              Object.keys(newInstance).forEach((key) => {
                if (key.toLowerCase() === 'id') return;
                const newVal = newInstance[key];
                const oldVal = matchingOldInstance[key];
                if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                  updatedInstance[key] = { classname: 'highlight-change', updatedValue: newVal, oldValue: oldVal };
                }
              });

              updatedInstance.id = safeId;
              changesInstances.push(updatedInstance);
            }
          });

          setChangesInstances(changesInstances);
          DebugService.log('Modification Log Component: Changes data', changesInstances);
          setShowLoading(false);
        })
        .catch((err) => {
          DebugService.log("Modification Log Component: Couldn't fetch data", err);
          setShowLoading(false);
        });
    }
  };

  useEffect(() => {
    setAddedInstances([]);
    setRemovedInstances([]);
    setChangesInstances([]);
    getLogs();
  }, [location.pathname, selectedAccount]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, modifiedEventsApiData);
  }, [modifiedEventsApiData]);

  return (
    <div style={{ paddingTop: '16px' }}>
      <>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newDate) => setFromDate(newDate.startOf('day'))}
                  slots={{ textField: (props) => <TextField {...props} variant="outlined" margin="normal" fullWidth size="small" /> }}
                  format="DD/MM/YYYY"
                  onClose={() => {}}
                  maxDate={dayjs(new Date(yesterdayDate()))}
                  disableFuture={true}
                />
              </DemoContainer>
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newDate) => setToDate(newDate.startOf('day'))}
                  slots={{ textField: (props) => <TextField {...props} variant="outlined" margin="normal" fullWidth size="small" /> }}
                  format="DD/MM/YYYY"
                  onClose={() => {}}
                  maxDate={dayjs(new Date(yesterdayDate()))}
                  disableFuture={true}
                />
              </DemoContainer>
            </LocalizationProvider>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
              <button
                className="btn primary"
                onClick={() => getLogs()}
                disabled={!toDate || !fromDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                Fetch Logs
              </button>
            </div>
          </div>

          <div className="card" style={{ borderRadius: '12px' }}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={(e, currentTab) => setSelectedTab(currentTab)} aria-label="Inventory Modification Tab List">
                  <Tab
                    label={`${INVENTORY_EVENTS.MODIFICATION_CHANGES} ${changesInstances?.length === 0 ? '(0)' : ''}`}
                    value={INVENTORY_EVENTS.MODIFICATION_CHANGES}
                    style={{ textTransform: 'none' }}
                  />
                  <Tab
                    label={`${INVENTORY_EVENTS.MODIFICATION_ADDED} ${addedInstances?.length === 0 ? '(0)' : ''}`}
                    value={INVENTORY_EVENTS.MODIFICATION_ADDED}
                    style={{ textTransform: 'none' }}
                  />
                  <Tab
                    label={`${INVENTORY_EVENTS.MODIFICATION_REMOVED} ${removedInstances?.length === 0 ? '(0)' : ''}`}
                    value={INVENTORY_EVENTS.MODIFICATION_REMOVED}
                    style={{ textTransform: 'none' }}
                  />
                </TabList>
              </Box>
              <TabPanel value={INVENTORY_EVENTS.MODIFICATION_CHANGES} sx={{ p: 0, pt: 1 }}>
                <CustomTable
                  rows={changesInstances}
                  columns={mapGridColumnsToTableColumns(changesColumns)}
                  loading={showLoading}
                  exportFilename={buildExportFilename(`modification_changes_${type}`, selectedAccount)}
                  subscriptionGated
                />
              </TabPanel>
              <TabPanel value={INVENTORY_EVENTS.MODIFICATION_ADDED} sx={{ p: 0, pt: 1 }}>
                <CustomTable
                  rows={addedInstances}
                  columns={mapGridColumnsToTableColumns(addedColumns)}
                  loading={showLoading}
                  exportFilename={buildExportFilename(`modification_added_${type}`, selectedAccount)}
                  subscriptionGated
                />
              </TabPanel>
              <TabPanel value={INVENTORY_EVENTS.MODIFICATION_REMOVED} sx={{ p: 0, pt: 1 }}>
                <CustomTable
                  rows={removedInstances}
                  columns={mapGridColumnsToTableColumns(addedColumns)}
                  loading={showLoading}
                  exportFilename={buildExportFilename(`modification_removed_${type}`, selectedAccount)}
                  subscriptionGated
                />
              </TabPanel>
            </TabContext>
          </div>

          {showSnackbarMessage === 'SHOW' && (
            <SnackbarNotification
              initialOpen={true}
              duration={7000}
              message="No changes found for the selected date"
              actionButtonName="Ok"
              theme={SNACKBAR_THEME.RED}
              yPosition="top"
              xPosition="center"
            />
          )}
      </>
    </div>
  );
}
